import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { fmt12 } from "../lib/time";
import { JOBBER_BOOKING_URL } from "../lib/brand";
import { CheckCircle2, Loader2, XCircle, Flame, CalendarPlus } from "lucide-react";

const BookingSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", booking: null, attempts: 0 });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", booking: null, attempts: 0 }); return; }
    let active = true;
    const MAX_ATTEMPTS = 60; // ~2.5 minutes (60 * 2.5s)
    const poll = async () => {
      try {
        const r = await api.get(`/bookings/status/${sessionId}`);
        if (!active) return;
        if (r.data.payment_status === "paid") {
          setState({ status: "paid", booking: r.data.booking, attempts: attemptsRef.current });
          return;
        }
        if (r.data.status === "expired") {
          setState({ status: "expired", booking: r.data.booking, attempts: attemptsRef.current });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "verifying", booking: r.data.booking, attempts: attemptsRef.current });
          return;
        }
        setTimeout(poll, 2500);
      } catch (e) {
        if (!active) return;
        setState({ status: "error", booking: null, attempts: attemptsRef.current });
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-[80vh] flex items-center" data-testid="booking-success-page">
      <div className="max-w-2xl mx-auto px-6 md:px-12 py-24 w-full">
        <div className="border border-[#262626] bg-[#0a0a0a] p-10 md:p-14">
          {state.status === "polling" && (
            <>
              <Loader2 size={48} className="text-[#D90429] animate-spin" />
              <h1 className="mt-6 font-display text-3xl font-bold uppercase tracking-tight">Confirming payment…</h1>
              <p className="mt-3 text-[#A3A3A3]">Hold tight, we're verifying with Stripe.</p>
            </>
          )}
          {state.status === "paid" && (
            <>
              <CheckCircle2 size={48} className="text-[#10B981]" />
              <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mt-6">Confirmed</div>
              <h1 className="mt-2 font-display text-4xl font-black uppercase tracking-tighter">Booking locked in. <span className="text-[#D90429]">Get ready to shine.</span></h1>
              <p className="mt-4 text-[#A3A3A3]">A confirmation has been sent to <span className="text-white">{state.booking?.email}</span>.</p>
              {state.booking && (
                <div className="mt-8 border border-[#262626] divide-y divide-[#1a1a1a]">
                  <Row label="Service" value={`${state.booking.service_name}${state.booking.vehicle_type ? ` (${state.booking.vehicle_type})` : ""}`} />
                  <Row label="Date" value={state.booking.date} />
                  <Row label="Time" value={fmt12(state.booking.time)} />
                  <Row label="Vehicle" value={state.booking.vehicle_make_model} />
                  <Row label="Service Total" value={`$${state.booking.amount.toFixed(2)}`} />
                  <Row label="Deposit Paid" value={`$${(state.booking.deposit_amount ?? state.booking.amount).toFixed(2)}`} highlight={state.booking.balance_amount > 0 ? false : true} />
                  {state.booking.balance_amount > 0 && (
                    <Row label="Balance Due After Service" value={`$${state.booking.balance_amount.toFixed(2)}`} highlight />
                  )}
                  {state.booking.service_address && <Row label="Service Address" value={state.booking.service_address} />}
                </div>
              )}
              {state.booking?.balance_amount > 0 && (
                <p className="mt-4 text-sm text-[#A3A3A3]">
                  You'll receive an invoice email with a secure Stripe link to pay the balance after your appointment.
                </p>
              )}
              <Link to="/" data-testid="success-home-link" className="mt-10 inline-flex items-center gap-2 bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase px-8 py-4 transition-colors">
                <Flame size={16}/> Back to home
              </Link>
              {state.booking?.id && (
                <a
                  href={`${API}/bookings/${state.booking.id}/calendar.ics`}
                  data-testid="success-add-to-calendar-btn"
                  className="mt-4 ml-0 sm:ml-3 inline-flex items-center gap-2 border border-[#262626] hover:border-[#D90429] text-white font-display font-bold tracking-wider uppercase px-8 py-4 transition-colors"
                >
                  <CalendarPlus size={16}/> Add to calendar
                </a>
              )}
            </>
          )}
          {(state.status === "expired" || state.status === "verifying" || state.status === "error") && (
            <>
              {state.status === "verifying" ? (
                <>
                  <Loader2 size={48} className="text-[#D90429]" />
                  <h1 className="mt-6 font-display text-3xl font-bold uppercase tracking-tight">Still verifying with your bank…</h1>
                  <p className="mt-3 text-[#A3A3A3]">Your payment is being processed. This sometimes happens when your bank requires extra verification (3D Secure). <strong className="text-white">If money left your account, your booking IS confirmed</strong> — you'll receive an email from <span className="text-[#D90429]">bookings@fuegodetailing.org</span> within a few minutes once Stripe finalizes.</p>
                  <p className="mt-4 text-sm text-[#A3A3A3]">Need help? Reply to the confirmation email or text us. Booking ID will be in the email.</p>
                </>
              ) : (
                <>
                  <XCircle size={48} className="text-[#EF4444]" />
                  <h1 className="mt-6 font-display text-3xl font-bold uppercase tracking-tight">Payment {state.status === "expired" ? "expired" : "could not be verified"}</h1>
                  <p className="mt-3 text-[#A3A3A3]">If money was charged, your booking will still process — you'll get a confirmation email shortly. If not, please try again.</p>
                </>
              )}
              <a href={JOBBER_BOOKING_URL} className="mt-8 inline-flex items-center gap-2 bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase px-8 py-4 transition-colors">
                {state.status === "verifying" ? "Back home" : "Try again"}
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, highlight }) => (
  <div className="flex justify-between px-5 py-3">
    <span className="text-[#A3A3A3] text-sm">{label}</span>
    <span className={`text-sm ${highlight ? "text-[#D90429] font-bold text-lg" : "text-white"}`}>{value}</span>
  </div>
);

export default BookingSuccessPage;
